import React, { useState, useEffect, useCallback } from 'react'
import { apiLabs, apiSantoLog } from '../../../Services/api'
import Dashboard from '../../../Components/Dashboard'
import { store } from '../../../store'
import history from '../../../Services/history'
import Denied from '../../../Components/Denied'
import {
  GlobalStyles, PageWrap, Topbar, Body,
  FilterBar, FilterGroup, FilterLabel, FilterInput, FilterSelect, FilterActions,
  TableWrap, Table, TableHead, TableRow, RowBtn, Chip,
  AlertList, AlertCard,
  Empty, Loading, Pagination,
  ModalOverlay, ModalBox, ModalHead, ModalBody, DetailGrid, PayloadBox,
  MODULE_COLORS,
} from './style'

const MODULES = [
  'recepcao', 'conferencia', 'separacao', 'cadastro', 'embarque',
  'devolucao', 'transferencia', 'romaneio', 'admin', 'webhook',
]

const ENTITY_TYPES = [
  'order', 'volume', 'box', 'driver', 'carrier', 'area', 'holiday', 'bulk_import',
]

const PER_PAGE_OPTIONS = [50, 100, 200, 500]

const EMPTY_FILTERS = {
  de: '',
  ate: '',
  user_id: '',
  module: '',
  action: '',
  entity_type: '',
  entity_id: '',
  order_number: '',
  rastreio: '',
  outcome: '',
}

// Formata ISO 2026-07-13T14:22:33.412Z → "13/07/2026 14:22:33"
function fmtDateTime(iso) {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    if (isNaN(d.getTime())) return String(iso)
    const dd = String(d.getDate()).padStart(2, '0')
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const yy = d.getFullYear()
    const hh = String(d.getHours()).padStart(2, '0')
    const mi = String(d.getMinutes()).padStart(2, '0')
    const ss = String(d.getSeconds()).padStart(2, '0')
    return `${dd}/${mm}/${yy} ${hh}:${mi}:${ss}`
  } catch (_) {
    return String(iso)
  }
}

function moduleChip(module) {
  const cfg = MODULE_COLORS[module] || {}
  return (
    <Chip $bg={cfg.bg} $color={cfg.color}>{module || '—'}</Chip>
  )
}

// Chip do resultado (success verde / divergence vermelho)
function outcomeChip(outcome) {
  if (outcome === 'divergence') {
    return <Chip $bg="rgba(239,68,68,.15)" $color="#f87171">⚠ divergência</Chip>
  }
  return <Chip $bg="rgba(0,229,160,.15)" $color="#00e5a0">✓ sucesso</Chip>
}

// Descrição legível do evento, montada a partir do payload. O backend já manda
// os nomes (order_number, box_name, driver_name), então não precisa lookup por UUID.
function describeEvent(it) {
  const p = it.payload || {}
  const order = p.order_number || (it.entity_type === 'order' ? it.entity_ref : null)
  const box = p.box_name || null
  const driver = p.driver_name || null
  const rastreio = p.rastreio || (it.entity_type === 'volume' ? it.entity_ref : null)
  const a = String(it.action || '')

  if (a === 'volume.read') return rastreio ? `Consultou volume ${rastreio}` : 'Consultou volume'
  if (a === 'order.read') return order ? `Consultou pedido ${order}` : 'Consultou pedido'
  if (a === 'box.read') return box ? `Bipou box ${box}` : 'Bipou box'
  if (a.startsWith('volume.embark')) {
    const bits = ['Embarcou']
    if (rastreio) bits.push(`volume ${rastreio}`)
    if (box) bits.push(`no box ${box}`)
    if (order) bits.push(`do pedido ${order}`)
    if (driver) bits.push(`· ${driver}`)
    return bits.join(' ')
  }
  if (a === 'order.status.change') return order ? `Status do pedido ${order}` : 'Mudança de status'
  if (a === 'order.edit') return order ? `Editou pedido ${order}` : 'Editou pedido'

  // genérico: junta as referências disponíveis
  const parts = [
    order ? `pedido ${order}` : null,
    box ? `box ${box}` : null,
    rastreio || null,
    driver ? `motorista ${driver}` : null,
  ].filter(Boolean)
  return parts.length ? parts.join(' · ') : '—'
}

// Rótulo legível do sufixo de um campo scanned_/expected_/actual_ do payload.
const FIELD_LABELS = {
  box: 'Box', box_name: 'Box',
  rastreio: 'Rastreio', volume: 'Rastreio', volume_rastreio: 'Rastreio',
  order: 'Pedido', order_number: 'Pedido',
  driver: 'Motorista', driver_name: 'Motorista',
  carrier: 'Transportadora', carrier_name: 'Transportadora',
  status: 'Status',
}
function humanizeField(suffix) {
  if (FIELD_LABELS[suffix]) return FIELD_LABELS[suffix]
  return suffix.replace(/_/g, ' ').replace(/^\w/, (ch) => ch.toUpperCase())
}
// Campos do payload com um prefixo (scanned_/expected_/actual_); ignora *_id (UUID).
function pickPrefixed(payload, prefix) {
  if (!payload) return []
  return Object.keys(payload)
    .filter((k) => k.startsWith(prefix) && !k.endsWith('_id'))
    .map((k) => ({ label: humanizeField(k.slice(prefix.length)), value: payload[k] }))
    .filter((x) => x.value != null && x.value !== '')
}
// Junta os campos como "Rótulo valor", sem duplicar quando o valor já traz o rótulo
// (ex.: box_name = "Box 009" → "Box 009", não "Box Box 009").
const joinFields = (arr) =>
  arr
    .map((f) => {
      const val = String(f.value)
      return val.toLowerCase().startsWith(String(f.label).toLowerCase()) ? val : `${f.label} ${val}`
    })
    .join(' · ')

export default function SantoLogAuditoria(props) {
  const { location: { idPage } } = props
  const [auth, setAuth] = useState(null)

  async function verifyAuth() {
    const { profile } = store.getState().user
    const res = await apiLabs.post('valid_key', { id: profile.id, menu_id: idPage })
    if (res.data.message === 'User not authorized') return setAuth('denied')
    if (res.data.message === 'User authorized') return setAuth('authorized')
    return history.push({ pathname: '/', state: { from: props.location } })
  }

  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(100)
  const [totalPages, setTotalPages] = useState(0)
  const [loading, setLoading] = useState(false)

  // Filtros aplicados (o que é enviado pro backend)
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS })
  // Filtros em edição (input controlado)
  const [draft, setDraft] = useState({ ...EMPTY_FILTERS })

  // Modal de detalhe
  const [detail, setDetail] = useState(null) // objeto do evento aberto

  useEffect(() => { verifyAuth() }, []) // eslint-disable-line

  // ── FETCH ──────────────────────────────────────────────────────────────
  const fetchAudit = useCallback(async () => {
    setLoading(true)
    try {
      const params = {}
      Object.entries(filters).forEach(([k, v]) => {
        if (v != null && String(v).trim() !== '') params[k] = String(v).trim()
      })
      params.page = page
      params.perPage = perPage
      const res = await apiSantoLog.get('/audit', { params })
      const data = res.data || {}
      setItems(Array.isArray(data.items) ? data.items : [])
      setTotal(Number(data.total) || 0)
      setTotalPages(Number(data.totalPages) || 0)
    } catch (err) {
      console.error('GET /audit failed:', err)
      setItems([])
      setTotal(0)
      setTotalPages(0)
    } finally {
      setLoading(false)
    }
  }, [filters, page, perPage])

  useEffect(() => {
    if (auth === 'authorized') fetchAudit()
  }, [auth, fetchAudit])

  function applyFilters() {
    setPage(1) // reset página ao aplicar novos filtros
    setFilters({ ...draft })
  }
  function clearFilters() {
    setDraft({ ...EMPTY_FILTERS })
    setFilters({ ...EMPTY_FILTERS })
    setPage(1)
  }
  function setDraftField(k, v) {
    setDraft(prev => ({ ...prev, [k]: v }))
  }

  // ── PAGINAÇÃO ─────────────────────────────────────────────────────────
  function goToPage(p) {
    if (p < 1 || (totalPages && p > totalPages) || p === page) return
    setPage(p)
  }

  // ── DETALHE ───────────────────────────────────────────────────────────
  async function openDetail(item) {
    // Já vem expandido na listagem, mas se faltar payload buscamos por :id
    if (item && item.payload) {
      setDetail(item)
      return
    }
    try {
      const res = await apiSantoLog.get(`/audit/${item.id}`)
      setDetail(res.data || item)
    } catch (_) {
      setDetail(item)
    }
  }
  function closeDetail() { setDetail(null) }

  // Cartão de alerta (divergência): "bipado" vs "correto" a partir do payload.
  function renderAlertCard(it) {
    const p = it.payload || {}
    const scanned = pickPrefixed(p, 'scanned_')
    const correct = [...pickPrefixed(p, 'expected_'), ...pickPrefixed(p, 'actual_')]
    return (
      <AlertCard key={it.id} onClick={() => openDetail(it)} title="Ver payload completo">
        <div className="ahead">
          <span className="tag">⚠ Alerta</span>
          <span className="time">{fmtDateTime(it.created_at)}</span>
          <span className="user">{it.user_name || 'Sistema'}</span>
          {moduleChip(it.module)}
          <span className="action">{it.action || ''}</span>
        </div>
        {p.reason && <span className="reason">{p.reason}</span>}
        <div className="msg">{p.message || describeEvent(it)}</div>
        {(scanned.length > 0 || correct.length > 0) && (
          <div className="cmp">
            {scanned.length > 0 && (
              <div className="row scanned">
                <span className="lbl">Bipado</span>
                <span className="val">{joinFields(scanned)}</span>
              </div>
            )}
            {correct.length > 0 && (
              <div className="row correct">
                <span className="lbl">Correto</span>
                <span className="val">{joinFields(correct)}</span>
              </div>
            )}
          </div>
        )}
      </AlertCard>
    )
  }

  // ── RENDER ────────────────────────────────────────────────────────────
  return (
    <Dashboard title="Auditoria">
      {auth === 'denied' && <Denied />}
      {auth === 'authorized' && (
        <>
          <GlobalStyles />
          <PageWrap>
            <Topbar>
              <h2>🕵 Auditoria</h2>
              <span className="crumb">Log imutável de todas as operações do SantoLog</span>
            </Topbar>

            <Body>
              {/* Filtros */}
              <FilterBar>
                <FilterGroup>
                  <FilterLabel>De</FilterLabel>
                  <FilterInput
                    type="date"
                    value={draft.de}
                    onChange={e => setDraftField('de', e.target.value)}
                  />
                </FilterGroup>
                <FilterGroup>
                  <FilterLabel>Até</FilterLabel>
                  <FilterInput
                    type="date"
                    value={draft.ate}
                    onChange={e => setDraftField('ate', e.target.value)}
                  />
                </FilterGroup>
                <FilterGroup>
                  <FilterLabel>Módulo</FilterLabel>
                  <FilterSelect
                    value={draft.module}
                    onChange={e => setDraftField('module', e.target.value)}
                  >
                    <option value="">Todos</option>
                    {MODULES.map(m => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </FilterSelect>
                </FilterGroup>
                <FilterGroup>
                  <FilterLabel>Resultado</FilterLabel>
                  <FilterSelect
                    value={draft.outcome}
                    onChange={e => setDraftField('outcome', e.target.value)}
                  >
                    <option value="">Todos</option>
                    <option value="success">✓ Sucesso</option>
                    <option value="divergence">⚠ Divergência</option>
                  </FilterSelect>
                </FilterGroup>
                <FilterGroup>
                  <FilterLabel>Tipo de entidade</FilterLabel>
                  <FilterSelect
                    value={draft.entity_type}
                    onChange={e => setDraftField('entity_type', e.target.value)}
                  >
                    <option value="">Todas</option>
                    {ENTITY_TYPES.map(e => (
                      <option key={e} value={e}>{e}</option>
                    ))}
                  </FilterSelect>
                </FilterGroup>
                <FilterGroup>
                  <FilterLabel>Ação (LIKE)</FilterLabel>
                  <FilterInput
                    placeholder="ex: volume.scan, order."
                    value={draft.action}
                    onChange={e => setDraftField('action', e.target.value)}
                  />
                </FilterGroup>
                <FilterGroup>
                  <FilterLabel>Pedido / NF</FilterLabel>
                  <FilterInput
                    placeholder="ex: VX18910087"
                    value={draft.order_number}
                    onChange={e => setDraftField('order_number', e.target.value)}
                  />
                </FilterGroup>
                <FilterGroup>
                  <FilterLabel>Rastreio</FilterLabel>
                  <FilterInput
                    placeholder="ex: ALEF123456789BR"
                    value={draft.rastreio}
                    onChange={e => setDraftField('rastreio', e.target.value)}
                  />
                </FilterGroup>
                <FilterGroup>
                  <FilterLabel>User ID (UUID)</FilterLabel>
                  <FilterInput
                    placeholder="uuid do usuário"
                    value={draft.user_id}
                    onChange={e => setDraftField('user_id', e.target.value)}
                  />
                </FilterGroup>
                <FilterGroup>
                  <FilterLabel>Entity ID</FilterLabel>
                  <FilterInput
                    placeholder="histórico de uma entidade específica"
                    value={draft.entity_id}
                    onChange={e => setDraftField('entity_id', e.target.value)}
                  />
                </FilterGroup>
              </FilterBar>

              <FilterActions>
                <span className="count">
                  <strong>{total}</strong> evento{total !== 1 ? 's' : ''} encontrado{total !== 1 ? 's' : ''}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    // Toggle rápido: liga/desliga o filtro de divergências e aplica na hora
                    const next = filters.outcome === 'divergence' ? '' : 'divergence';
                    setDraft(prev => ({ ...prev, outcome: next }));
                    setFilters(prev => ({ ...prev, outcome: next }));
                    setPage(1);
                  }}
                  disabled={loading}
                  className={filters.outcome === 'divergence' ? 'primary' : ''}
                  style={filters.outcome === 'divergence'
                    ? { background: '#ef4444', color: '#fff', border: 'none' }
                    : undefined}
                >
                  ⚠ {filters.outcome === 'divergence' ? 'Mostrando divergências' : 'Ver só divergências'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    // Toggle: filtra ações .read (consultas/bipagens) via LIKE 'read'
                    const next = filters.action === 'read' ? '' : 'read';
                    setDraft(prev => ({ ...prev, action: next }));
                    setFilters(prev => ({ ...prev, action: next }));
                    setPage(1);
                  }}
                  disabled={loading}
                  className={filters.action === 'read' ? 'primary' : ''}
                >
                  🔎 {filters.action === 'read' ? 'Mostrando consultas' : 'Ver só consultas'}
                </button>
                <span className="spacer" />
                <button type="button" onClick={clearFilters} disabled={loading}>
                  Limpar filtros
                </button>
                <button
                  type="button"
                  className="primary"
                  onClick={applyFilters}
                  disabled={loading}
                >
                  {loading ? '…' : 'Aplicar'}
                </button>
              </FilterActions>

              {/* Divergências viram cartões de alerta; o resto fica em tabela */}
              {filters.outcome === 'divergence' ? (
                <AlertList>
                  {loading && items.length === 0 && (
                    <Loading>Carregando alertas…</Loading>
                  )}
                  {!loading && items.length === 0 && (
                    <Empty>Nenhuma divergência encontrada — a operação está limpa 🎉</Empty>
                  )}
                  {items.map(renderAlertCard)}
                </AlertList>
              ) : (
              <TableWrap>
                <Table>
                  <TableHead>
                    <span>Data / Hora</span>
                    <span>Usuário</span>
                    <span>Módulo</span>
                    <span>Resultado</span>
                    <span>Ação</span>
                    <span>Entidade</span>
                    <span>Descrição</span>
                    <span>Transição</span>
                    <span />
                  </TableHead>

                  {loading && items.length === 0 && (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <Loading>Carregando eventos…</Loading>
                    </div>
                  )}
                  {!loading && items.length === 0 && (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <Empty>
                        {filters.outcome === 'divergence'
                          ? 'Nenhuma divergência encontrada — a operação está limpa 🎉'
                          : 'Nenhum evento encontrado com os filtros aplicados.'}
                      </Empty>
                    </div>
                  )}

                  {items.map((it) => (
                    <TableRow key={it.id}>
                      <span className="mono">{fmtDateTime(it.created_at)}</span>
                      <span className="dim">
                        {it.user_name
                          ? it.user_name
                          : (it.user_id ? (it.user_source || 'usuário') : 'Sistema')}
                      </span>
                      <span>{moduleChip(it.module)}</span>
                      <span>{outcomeChip(it.outcome)}</span>
                      <span className="actionCell">{it.action || '—'}</span>
                      <span className="entityCell">{it.entity_type || '—'}</span>
                      <span className="desc">{describeEvent(it)}</span>
                      <span className="transitionCell">
                        {it.from_status || it.to_status ? (
                          <>
                            <span className="from">{it.from_status || '—'}</span>
                            <span className="arrow">→</span>
                            <span className="to">{it.to_status || '—'}</span>
                          </>
                        ) : (
                          <span className="from">—</span>
                        )}
                      </span>
                      <span className="rowBtn">
                        <RowBtn
                          onClick={() => openDetail(it)}
                          title="Ver payload completo"
                        >
                          ⓘ
                        </RowBtn>
                      </span>
                    </TableRow>
                  ))}
                </Table>
              </TableWrap>
              )}

              {/* Paginação */}
              {totalPages > 0 && (
                <Pagination>
                  <button
                    type="button"
                    onClick={() => goToPage(1)}
                    disabled={page <= 1 || loading}
                  >
                    ‹‹ Início
                  </button>
                  <button
                    type="button"
                    onClick={() => goToPage(page - 1)}
                    disabled={page <= 1 || loading}
                  >
                    ‹ Anterior
                  </button>
                  <span>Página <span className="page">{page}</span> de <span className="page">{totalPages}</span></span>
                  <button
                    type="button"
                    onClick={() => goToPage(page + 1)}
                    disabled={page >= totalPages || loading}
                  >
                    Próxima ›
                  </button>
                  <button
                    type="button"
                    onClick={() => goToPage(totalPages)}
                    disabled={page >= totalPages || loading}
                  >
                    Fim ››
                  </button>
                  <span className="spacer" />
                  <span>Por página:</span>
                  <select
                    value={perPage}
                    onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1) }}
                    disabled={loading}
                  >
                    {PER_PAGE_OPTIONS.map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </Pagination>
              )}
            </Body>
          </PageWrap>

          {/* Modal de detalhe do evento */}
          <ModalOverlay $visible={!!detail} onClick={closeDetail}>
            {detail && (
              <ModalBox onClick={(e) => e.stopPropagation()}>
                <ModalHead>
                  <h3>Detalhe do evento</h3>
                  <button type="button" className="close" onClick={closeDetail}>✕</button>
                </ModalHead>
                <ModalBody>
                  {/* Banner de divergência — payload.reason + payload.message
                      já vêm humanizados do backend, exibimos direto no topo. */}
                  {detail.outcome === 'divergence' && detail.payload && (
                    <div style={{
                      marginBottom: 18,
                      padding: '12px 14px',
                      background: 'rgba(239,68,68,.08)',
                      border: '1px solid rgba(239,68,68,.35)',
                      borderRadius: 8,
                    }}>
                      <div style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: '#f87171',
                        textTransform: 'uppercase',
                        letterSpacing: '.06em',
                        marginBottom: 4,
                      }}>
                        ⚠ Divergência
                        {detail.payload.reason && (
                          <span style={{
                            marginLeft: 8,
                            fontFamily: "'Space Mono', monospace",
                            color: '#fca5a5',
                          }}>
                            · {detail.payload.reason}
                          </span>
                        )}
                      </div>
                      {detail.payload.message && (
                        <div style={{ fontSize: 13, color: '#e8ecf0', lineHeight: 1.5 }}>
                          {detail.payload.message}
                        </div>
                      )}
                    </div>
                  )}

                  <DetailGrid>
                    <div className="k">Data / Hora</div>
                    <div className="v mono">{fmtDateTime(detail.created_at)}</div>

                    <div className="k">Ação</div>
                    <div className="v mono">{detail.action || '—'}</div>

                    <div className="k">Módulo</div>
                    <div className="v">{moduleChip(detail.module)}</div>

                    <div className="k">Resultado</div>
                    <div className="v">{outcomeChip(detail.outcome)}</div>

                    <div className="k">Entidade</div>
                    <div className="v">
                      {detail.entity_type || '—'}
                      {detail.entity_id && (
                        <>
                          <span style={{ color: '#5a6370' }}> · </span>
                          <span className="mono" style={{ color: '#8a95a3' }}>{detail.entity_id}</span>
                        </>
                      )}
                    </div>

                    {detail.entity_ref && (
                      <>
                        <div className="k">Referência</div>
                        <div className="v mono">{detail.entity_ref}</div>
                      </>
                    )}

                    <div className="k">Usuário</div>
                    <div className="v">
                      {detail.user_name
                        ? (
                          <>
                            <strong style={{ color: '#e8ecf0' }}>{detail.user_name}</strong>
                            {detail.user_source && (
                              <span style={{ color: '#8a95a3', fontSize: 11, marginLeft: 8 }}>
                                {detail.user_source}
                              </span>
                            )}
                            {detail.user_id && (
                              <span className="mono" style={{ color: '#5a6370', fontSize: 10, marginLeft: 8 }}>
                                id {detail.user_id}
                              </span>
                            )}
                          </>
                        )
                        : (
                          <span style={{ color: '#8a95a3', fontStyle: 'italic' }}>
                            Sistema (webhook/job)
                          </span>
                        )}
                    </div>

                    {(detail.from_status || detail.to_status) && (
                      <>
                        <div className="k">Transição</div>
                        <div className="v mono">
                          <span style={{ color: '#8a95a3' }}>{detail.from_status || '—'}</span>
                          <span style={{ color: '#5a6370', margin: '0 6px' }}>→</span>
                          <span style={{ color: '#00e5a0', fontWeight: 700 }}>{detail.to_status || '—'}</span>
                        </div>
                      </>
                    )}

                    {detail.ip && (
                      <>
                        <div className="k">IP</div>
                        <div className="v mono">{detail.ip}</div>
                      </>
                    )}

                    <div className="k">ID do evento</div>
                    <div className="v mono">{detail.id}</div>
                  </DetailGrid>

                  {detail.payload && (
                    <>
                      <div style={{ marginTop: 18, fontSize: 10, color: '#5a6370', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em' }}>
                        Payload
                      </div>
                      <PayloadBox>{JSON.stringify(detail.payload, null, 2)}</PayloadBox>
                    </>
                  )}
                </ModalBody>
              </ModalBox>
            )}
          </ModalOverlay>
        </>
      )}
    </Dashboard>
  )
}
